import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Button, Title } from "lola-framework-ui-test";
import { FloatingField, type FloatingFieldColors } from "./FloatingField";
import { useStore } from "../store/globalState";
import { useCatalogPayers, useCatalogStates } from "../hooks/useCatalog";
import { resolveStateOptions, stateName } from "../utils/states";
import { useCashPickupHabits } from "../hooks/useCashPickupHabits";
import type { CashPickupHabit } from "../api/cash-pickup-habits";
import {
  bareStateCode,
  catalogPayerDisplayName,
  dedupeOfferableCashPayers,
  dedupeOfferableCashPayersForState,
  isWildcardStateCapability,
  payerCodesEquivalent,
  resolveCashPickupOption,
} from "../utils/payerCode";
import {
  bankPrefixMismatch,
  inspectBankAccountNumber,
} from "../utils/bankAccountNumber";
import { PayoutMethodType } from "../interfaces/payout";
import type { EstimateOption } from "../api/pricing";
import type { FundRequestPayoutInput } from "../api/fund-request";
import { BackArrowLight, CloseLight } from "../assets/navIcons";

const MONO_INPUT: CSSProperties = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  letterSpacing: "0.04em",
};

function payoutFieldColors(view: {
  background: string;
  title: string;
  bodyCopy: string;
  subtitile: string;
  errorColor?: string;
}): FloatingFieldColors {
  return {
    background: view.background,
    title: view.title,
    bodyCopy: view.bodyCopy,
    inactiveColor: view.subtitile,
    errorColor: view.errorColor,
  };
}

export interface PayoutDetailsResult {
  payout: FundRequestPayoutInput;
  /** Pre-quote option the selection priced against, when one matched. */
  option: EstimateOption | null;
}

export interface PayoutDetailsSlotProps {
  method: PayoutMethodType;
  /** Corridor origin — must match fund-request mint resolve. */
  originCountryCode: string;
  destinationCountry: string;
  /** Destination currency for catalog + mint parity. */
  destinationCurrency: string;
  /** Pre-quote options for the corridor — drives payer offerability + SKU. */
  options: EstimateOption[];
  optionsLoading?: boolean;
  /** Existing payout to seed the form when re-editing. */
  value?: FundRequestPayoutInput | null;
  onBack: () => void;
  onClose: () => void;
  onSubmit: (result: PayoutDetailsResult) => void;
}

/** Local account length expected per destination country (digits only). */
const ACCOUNT_LENGTH: Record<string, number> = { MX: 18, AR: 22, PE: 20 };
const MAX_ACCOUNT_NUMBER_LENGTH = 34;

/** The catalog payer code a built payout carries, if any. */
const payerCodeOf = (
  method: PayoutMethodType,
  payout: FundRequestPayoutInput
): string | null => {
  switch (method) {
    case PayoutMethodType.BANK_ACCOUNT:
      return payout.bankId ?? null;
    case PayoutMethodType.CASH_PICKUP:
      return payout.payerId ?? null;
    case PayoutMethodType.MOBILE_WALLET:
      return payout.carrierId ?? null;
    default:
      return null;
  }
};

/**
 * Resolves the pre-quote option for a selection. Cash uses cross-corridor payer
 * equivalence and state/ANY matching — never a silent wrong-SKU fallback.
 * Bank/card may fall back to the single method option when no payer is pinned.
 */
const resolveOption = (
  method: PayoutMethodType,
  payout: FundRequestPayoutInput,
  options: EstimateOption[]
): EstimateOption | null => {
  if (method === PayoutMethodType.CASH_PICKUP) {
    const payerCode = payout.payerId;
    if (!payerCode) return null;
    return resolveCashPickupOption(options, payerCode, payout.stateCode);
  }
  const forMethod = options.filter((o) => o.payoutMethod === method);
  const payerCode = payerCodeOf(method, payout);
  if (payerCode) {
    const byPayer = forMethod.find((o) =>
      payerCodesEquivalent(o.payerCode, payerCode)
    );
    if (byPayer) return byPayer;
    // Payer pinned but no match — do not bind an unrelated SKU.
    return null;
  }
  return forMethod[0] ?? null;
};

/**
 * Second stage of the payout picker. After the requester chooses HOW they want
 * to be paid (method), this collects the payer and the per-method fields the
 * fund-request mint requires: bank + account number, state + payer for cash,
 * carrier + phone for wallet. Debit card and saved bank accounts are picked on
 * the first payout popup; this slot is bank (new only) / cash / wallet.
 */
export const PayoutDetailsSlot: React.FC<PayoutDetailsSlotProps> = ({
  method,
  originCountryCode,
  destinationCountry,
  destinationCurrency,
  options,
  optionsLoading,
  value,
  onBack,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const colorConfig = theme.whiteView;
  const { bodyCopy } = colorConfig;

  const submit = (payout: FundRequestPayoutInput): void => {
    onSubmit({ payout, option: resolveOption(method, payout, options) });
  };

  const routeScope = {
    originCountryCode,
    destinationCountry,
    destinationCurrency,
  };

  return (
    <section className="payout-details-slot">
      <header className="payout-details-header">
        <button
          type="button"
          className="payout-header-action payout-back-btn"
          aria-label={t("common.back")}
          onClick={onBack}
        >
          <BackArrowLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
        <Title
          title={t(`payout.details.${method}.title`)}
          align="center"
          color={bodyCopy}
        />
        <button
          type="button"
          className="payout-header-action payout-close-btn"
          aria-label="Close"
          onClick={onClose}
        >
          <CloseLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
      </header>

      {method === PayoutMethodType.BANK_ACCOUNT && (
        <BankAccountForm
          {...routeScope}
          options={options}
          optionsLoading={optionsLoading}
          value={value}
          onSubmit={submit}
        />
      )}
      {method === PayoutMethodType.CASH_PICKUP && (
        <CashPickupForm
          {...routeScope}
          options={options}
          optionsLoading={optionsLoading}
          value={value}
          onSubmit={submit}
        />
      )}
      {method === PayoutMethodType.MOBILE_WALLET && (
        <MobileWalletForm
          {...routeScope}
          options={options}
          optionsLoading={optionsLoading}
          value={value}
          onSubmit={submit}
        />
      )}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Sub-forms
// ---------------------------------------------------------------------------

const sanitizeDigits = (raw: string, length?: number): string => {
  const digits = raw.replace(/\D/g, "");
  return length != null ? digits.slice(0, length) : digits;
};

const sanitizeAccountNumber = (raw: string, exactLength?: number): string => {
  if (exactLength != null) return sanitizeDigits(raw, exactLength);
  return raw
    .replace(/\s/g, "")
    .replace(/[^a-z0-9]/gi, "")
    .toUpperCase()
    .slice(0, MAX_ACCOUNT_NUMBER_LENGTH);
};

interface RouteScopedFormProps {
  originCountryCode: string;
  destinationCountry: string;
  destinationCurrency: string;
  options: EstimateOption[];
  optionsLoading?: boolean;
  value?: FundRequestPayoutInput | null;
  onSubmit: (payout: FundRequestPayoutInput) => void;
}

const BankAccountForm: React.FC<RouteScopedFormProps> = ({
  originCountryCode,
  destinationCountry,
  destinationCurrency,
  options,
  optionsLoading,
  value,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { backgroundBtn, textColorBtn, bodyCopy, errorColor } = theme.whiteView;
  const fieldColors = payoutFieldColors(theme.whiteView);
  const { data: payers, loading } = useCatalogPayers({
    originCountryCode,
    destinationCountryCode: destinationCountry,
    payoutMethod: "BANK_ACCOUNT",
    currencyCode: destinationCurrency,
  });

  const methodOptions = useMemo(
    () => options.filter((o) => o.payoutMethod === "BANK_ACCOUNT"),
    [options]
  );
  const offerablePayerCodes = useMemo(() => {
    const set = new Set<string>();
    for (const o of methodOptions) {
      if (o.payerCode) set.add(o.payerCode);
    }
    return set;
  }, [methodOptions]);

  const banks = useMemo(() => {
    // Wait for pre-quote so we never flash a broader catalog than mint allows.
    if (optionsLoading) return [];
    // No BANK_ACCOUNT SKU for this route/amount → nothing to pick.
    if (methodOptions.length === 0) return [];
    const available = (payers ?? []).filter(
      (p) => p.effectiveStatus === "AVAILABLE"
    );
    // When pre-quote varies by payer, intersect; when it returns a generic
    // method SKU (no payerCode), keep route-scoped AVAILABLE banks.
    const scoped =
      offerablePayerCodes.size > 0
        ? available.filter((p) => offerablePayerCodes.has(p.payerCode))
        : available;
    return scoped
      .map((p) => {
        const name = catalogPayerDisplayName(p.payerCode, payers);
        return name
          ? { id: p.payerCode, name, accountPrefixes: p.accountPrefixes }
          : null;
      })
      .filter(
        (row): row is { id: string; name: string; accountPrefixes: string[] | undefined } =>
          row != null
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [payers, optionsLoading, methodOptions, offerablePayerCodes]);

  const [bankId, setBankId] = useState<string | null>(value?.bankId ?? null);
  const [accountNumber, setAccountNumber] = useState<string>(
    value?.accountNumber ?? ""
  );
  const expectedLength = ACCOUNT_LENGTH[destinationCountry.toUpperCase()];
  const identifier = sanitizeAccountNumber(accountNumber, expectedLength);
  const inspection = inspectBankAccountNumber(destinationCountry, identifier);
  const selectedPrefixes = banks.find((b) => b.id === bankId)?.accountPrefixes;
  const mismatch =
    inspection.canContinue &&
    bankPrefixMismatch(inspection.bankCode, selectedPrefixes);
  const banksLoading = Boolean(optionsLoading) || loading;
  const valid =
    Boolean(bankId) &&
    inspection.canContinue &&
    banks.some((b) => b.id === bankId);

  return (
    <form
      className="payout-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || !bankId) return;
        onSubmit({ bankId, accountNumber: identifier });
      }}
    >
      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.bank")}
          value={bankId ?? ""}
          colors={fieldColors}
        >
          <select
            style={{ color: bodyCopy, cursor: "pointer" }}
            value={bankId ?? ""}
            disabled={banksLoading}
            onChange={(e) => {
              setBankId(e.target.value || null);
            }}
          >
            <option value="" />
            {banks.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </FloatingField>
        {banksLoading && (
          <span className="payout-hint">{t("payout.fields.payerLoading")}</span>
        )}
        {!banksLoading && banks.length === 0 && (
          <span className="payout-hint">{t("payout.fields.noBanks")}</span>
        )}
      </div>

      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.accountNumber")}
          value={identifier}
          colors={fieldColors}
        >
          <input
            type="text"
            inputMode={expectedLength == null ? "text" : "numeric"}
            maxLength={expectedLength ?? MAX_ACCOUNT_NUMBER_LENGTH}
            autoComplete="off"
            style={{ color: bodyCopy, ...MONO_INPUT }}
            value={identifier}
            onChange={(e) => {
              setAccountNumber(
                sanitizeAccountNumber(e.target.value, expectedLength)
              );
            }}
          />
        </FloatingField>
        {expectedLength != null && (
          <span className="payout-hint">
            {identifier.length} / {expectedLength}
          </span>
        )}
        {identifier.length > 0 && !inspection.formatOk && (
          <span className="payout-hint" style={{ color: errorColor }}>
            {t("payout.fields.accountNumberInvalid")}
          </span>
        )}
        {mismatch && (
          <span className="payout-hint" style={{ color: errorColor }}>
            {t("payout.fields.bankMismatch")}
          </span>
        )}
      </div>

      <Button
        type="submit"
        size="large"
        disabled={!valid}
        background={backgroundBtn}
        color={textColorBtn}
      >
        {mismatch
          ? t("payout.fields.bankMismatchConfirm")
          : t("common.continue")}
      </Button>
    </form>
  );
};

const CashPickupForm: React.FC<RouteScopedFormProps> = ({
  originCountryCode,
  destinationCountry,
  destinationCurrency,
  options,
  optionsLoading,
  value,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { backgroundBtn, textColorBtn, bodyCopy } = theme.whiteView;
  const fieldColors = payoutFieldColors(theme.whiteView);
  const { habits: remoteHabits, loading: habitsLoading } =
    useCashPickupHabits(destinationCountry);

  const catalogScope = {
    originCountryCode,
    currencyCode: destinationCurrency,
  };

  // Corridor-wide offerable networks (habits / empty-state). The picker uses
  // the state-scoped set below so Continuar never sees an unresolvable pair.
  const offerablePayerCodes = useMemo(
    () => new Set(dedupeOfferableCashPayers(options)),
    [options]
  );

  // INCOMM (and other push-funding) cash SKUs are `…:ANY:ANY:PAYER`. When any
  // offerable option is state-agnostic, force ANY-state UI so we do not mix in
  // CRB state-scoped MX_* catalog rows that hide short INCOMM codes.
  const optionsPreferAnyState = useMemo(
    () =>
      options.some(
        (o) =>
          o.payoutMethod === "CASH_PICKUP" &&
          isWildcardStateCapability(o.capabilityKey)
      ),
    [options]
  );

  const { data: states, loading: statesLoading } = useCatalogStates(
    destinationCountry,
    { payoutMethod: "CASH_PICKUP", originCountryCode },
    !optionsPreferAnyState
  );

  // Country-wide payers — labels only. Offerability comes from pre-quote options.
  const { data: countryPayers, loading: countryPayersLoading } =
    useCatalogPayers({
      ...catalogScope,
      destinationCountryCode: destinationCountry,
      payoutMethod: "CASH_PICKUP",
    });

  const availableCountryPayers = useMemo(
    () => (countryPayers ?? []).filter((p) => p.effectiveStatus === "AVAILABLE"),
    [countryPayers]
  );

  const isAnyStateMode =
    optionsPreferAnyState ||
    (!statesLoading &&
      (states ?? []).length === 0 &&
      offerablePayerCodes.size > 0);

  const [stateCode, setStateCode] = useState<string | null>(
    value?.stateCode ?? null
  );
  const [payerId, setPayerId] = useState<string | null>(value?.payerId ?? null);

  // Only payers that `resolveCashPickupOption` can bind for this state
  // (ANY:ANY or matching state SKU). Prevents showing MX_7ELEVEN for AGU when
  // the pre-quote only has e.g. JAL coverage for that network.
  const offerablePayerCodesForState = useMemo(
    () => new Set(dedupeOfferableCashPayersForState(options, stateCode)),
    [options, stateCode]
  );

  // Drop subdivisions with no reachable payer: same gate the payer list and
  // Continuar apply, so the picker never leads to an empty payer select.
  // ANY-state mode is exempt — a country-wide SKU covers every subdivision.
  const stateOptions = useMemo(() => {
    const all = resolveStateOptions(destinationCountry, states, isAnyStateMode);
    if (isAnyStateMode || optionsLoading || offerablePayerCodes.size === 0) {
      return all;
    }
    const catalogPayersByState = new Map(
      (states ?? []).map((s) => [
        s.stateCode.toUpperCase(),
        s.payerCodes ?? [],
      ])
    );
    return all.filter((s) => {
      if (dedupeOfferableCashPayersForState(options, s.code).length > 0) {
        return true;
      }
      // Mirrors the catalog fallback in `payerList`: a state-scoped SKU may
      // price while the catalog is the one naming the network.
      const catalogCodes = catalogPayersByState.get(s.code.toUpperCase()) ?? [];
      return catalogCodes.some((code) =>
        [...offerablePayerCodes].some((offerable) =>
          payerCodesEquivalent(offerable, code)
        )
      );
    });
  }, [
    destinationCountry,
    states,
    isAnyStateMode,
    optionsLoading,
    options,
    offerablePayerCodes,
  ]);

  // A state filtered out by a fresh pre-quote (e.g. after changing the amount)
  // must not stay selected with an unresolvable payer behind it.
  useEffect(() => {
    if (!stateCode) return;
    if (stateOptions.length === 0) return;
    const stillOffered = stateOptions.some(
      (s) => s.code.toUpperCase() === stateCode.toUpperCase()
    );
    if (!stillOffered) {
      setStateCode(null);
      setPayerId(null);
    }
  }, [stateOptions, stateCode]);

  const stateNameByCode = useMemo(
    () => new Map(stateOptions.map((s) => [s.code.toUpperCase(), s.name])),
    [stateOptions]
  );

  // Scoped catalog labels when not in ANY-state (skipped otherwise — equality
  // match on stateCode would drop state-agnostic payers).
  const { data: scopedPayers, loading: scopedPayersLoading } = useCatalogPayers(
    !isAnyStateMode && stateCode
      ? {
          ...catalogScope,
          destinationCountryCode: destinationCountry,
          payoutMethod: "CASH_PICKUP",
          stateCode,
        }
      : null
  );

  const payerCatalog = useMemo(
    () => [...availableCountryPayers, ...(scopedPayers ?? [])],
    [availableCountryPayers, scopedPayers]
  );

  const savedHabits = useMemo(() => {
    if (optionsLoading) return [];
    return remoteHabits.filter((h) =>
      [...offerablePayerCodes].some((code) =>
        payerCodesEquivalent(code, h.payerId)
      )
    );
  }, [remoteHabits, destinationCountry, offerablePayerCodes, optionsLoading]);

  const payerList = useMemo(() => {
    if (optionsLoading || !stateCode) return [];
    const fromQuote = [...offerablePayerCodesForState];
    if (fromQuote.length > 0) {
      return fromQuote
        .map((code) => {
          const name = catalogPayerDisplayName(code, payerCatalog);
          return name ? { id: code, name } : null;
        })
        .filter((row): row is { id: string; name: string } => row != null)
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    const source = isAnyStateMode ? availableCountryPayers : scopedPayers ?? [];
    return source
      .filter((p) => p.effectiveStatus === "AVAILABLE")
      .filter(
        (p) =>
          offerablePayerCodes.size === 0 ||
          [...offerablePayerCodes].some((code) =>
            payerCodesEquivalent(code, p.payerCode)
          )
      )
      .map((p) => {
        const name = catalogPayerDisplayName(p.payerCode, payerCatalog);
        return name ? { id: p.payerCode, name } : null;
      })
      .filter((row): row is { id: string; name: string } => row != null)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [
    optionsLoading,
    stateCode,
    offerablePayerCodesForState,
    payerCatalog,
    isAnyStateMode,
    availableCountryPayers,
    scopedPayers,
    offerablePayerCodes,
  ]);

  // A payer that isn't in the current list (e.g. after switching state) is
  // treated as unselected; the select reflects this and submit stays gated.
  // Habits may store MX_ELEKTRA while the list shows ELEKTRA — map to list id.
  const effectivePayerId = useMemo(() => {
    if (!payerId) return null;
    const exact = payerList.find((p) => p.id === payerId);
    if (exact) return exact.id;
    const aliased = payerList.find((p) =>
      payerCodesEquivalent(p.id, payerId)
    );
    return aliased?.id ?? null;
  }, [payerId, payerList]);

  const payersLoading =
    Boolean(optionsLoading) ||
    (isAnyStateMode
      ? countryPayersLoading
      : Boolean(stateCode) && scopedPayersLoading);

  // Block confirm when state+payer cannot bind a quotable SKU (e.g. CRB
  // state-scoped payer + ISO state the catalog does not cover).
  const catalogCoversPayer = Boolean(
    effectivePayerId &&
      (isAnyStateMode ? availableCountryPayers : scopedPayers ?? []).some(
        (p) =>
          p.effectiveStatus === "AVAILABLE" &&
          payerCodesEquivalent(p.payerCode, effectivePayerId)
      )
  );

  const canResolveSku = Boolean(
    stateCode &&
      effectivePayerId &&
      resolveCashPickupOption(options, effectivePayerId, stateCode)
  );

  const valid = Boolean(canResolveSku || (stateCode && catalogCoversPayer));

  const labeledHabits = useMemo(() => {
    if (countryPayersLoading || countryPayers == null) return [];
    return savedHabits
      .map((habit) => {
        const payerLabel = catalogPayerDisplayName(habit.payerId, countryPayers);
        const bare = bareStateCode(habit.stateCode) ?? habit.stateCode;
        const stateLabel =
          stateNameByCode.get(bare.toUpperCase()) ||
          stateName(destinationCountry, bare) ||
          bare;
        if (!payerLabel || !stateLabel) return null;
        return { habit, title: payerLabel, subtitle: stateLabel };
      })
      .filter((row): row is NonNullable<typeof row> => row != null);
  }, [
    savedHabits,
    countryPayers,
    countryPayersLoading,
    stateNameByCode,
    destinationCountry,
  ]);

  const habitsBusy =
    habitsLoading || optionsLoading || countryPayersLoading;
  const showSavedBlock = habitsBusy || labeledHabits.length > 0;

  const commitCash = (nextState: string, nextPayer: string) => {
    onSubmit({ stateCode: nextState, payerId: nextPayer });
  };

  const selectHabit = (habit: CashPickupHabit) => {
    const bare = bareStateCode(habit.stateCode) ?? habit.stateCode;
    commitCash(bare, habit.payerId);
  };

  return (
    <form
      className="payout-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || !stateCode || !effectivePayerId) return;
        commitCash(stateCode, effectivePayerId);
      }}
    >
      {showSavedBlock && (
        <section
          className="payout-saved"
          aria-label={t("payout.savedDestinations.aria")}
          aria-busy={habitsBusy}
        >
          <span className="payout-field-label">
            {t("payout.savedDestinations.label")}
          </span>
          <div role="list" className="payout-saved-list">
            {habitsBusy ? (
              <>
                {Array.from({ length: 2 }, (_, i) => (
                  <div
                    key={i}
                    className="payout-saved-skeleton"
                    aria-hidden="true"
                  >
                    <span className="payout-saved-skeleton-line" />
                    <span className="payout-saved-skeleton-line short" />
                  </div>
                ))}
              </>
            ) : (
              labeledHabits.map(({ habit, title, subtitle }) => (
                <button
                  key={`${habit.stateCode}:${habit.payerId}`}
                  type="button"
                  role="listitem"
                  className="payout-saved-item"
                  style={{ color: bodyCopy }}
                  onClick={() => selectHabit(habit)}
                >
                  <span className="payout-saved-title">{title}</span>
                  <span
                    className="payout-hint"
                    style={{ color: bodyCopy, opacity: 0.55 }}
                  >
                    {subtitle}
                  </span>
                </button>
              ))
            )}
          </div>
          <span className="payout-hint payout-or-new">
            {t("payout.savedDestinations.orNew")}
          </span>
        </section>
      )}

      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.state")}
          value={stateCode ?? ""}
          colors={fieldColors}
        >
          <select
            style={{ color: bodyCopy, cursor: "pointer" }}
            value={stateCode ?? ""}
            onChange={(e) => {
              setStateCode(e.target.value || null);
              // Switching state invalidates the previously-chosen payer.
              setPayerId(null);
            }}
          >
            <option value="" />
            {stateOptions.map((s) => (
              <option key={`${destinationCountry}-${s.code}`} value={s.code}>
                {s.name}
              </option>
            ))}
          </select>
        </FloatingField>
        {!statesLoading && !optionsLoading && stateOptions.length === 0 && (
          <span className="payout-hint">{t("payout.fields.noStates")}</span>
        )}
      </div>

      {stateCode && (
        <div className="payout-field">
          <FloatingField
            label={t("payout.fields.payer")}
            value={effectivePayerId ?? ""}
            colors={fieldColors}
          >
            <select
              style={{ color: bodyCopy, cursor: "pointer" }}
              value={effectivePayerId ?? ""}
              disabled={payersLoading}
              onChange={(e) => setPayerId(e.target.value || null)}
            >
              <option value="" />
              {payerList.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </FloatingField>
          {payersLoading && (
            <span className="payout-hint">{t("payout.fields.payerLoading")}</span>
          )}
          {!payersLoading && payerList.length === 0 && (
            <span className="payout-hint">{t("payout.fields.noPayers")}</span>
          )}
        </div>
      )}

      <Button
        type="submit"
        size="large"
        disabled={!valid}
        background={backgroundBtn}
        color={textColorBtn}
      >
        {t("common.continue")}
      </Button>
    </form>
  );
};

const MobileWalletForm: React.FC<RouteScopedFormProps> = ({
  originCountryCode,
  destinationCountry,
  destinationCurrency,
  options,
  optionsLoading,
  value,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { backgroundBtn, textColorBtn, bodyCopy } = theme.whiteView;
  const fieldColors = payoutFieldColors(theme.whiteView);
  const { data: payers, loading } = useCatalogPayers({
    originCountryCode,
    destinationCountryCode: destinationCountry,
    payoutMethod: "MOBILE_WALLET",
    currencyCode: destinationCurrency,
  });

  const methodOptions = useMemo(
    () => options.filter((o) => o.payoutMethod === "MOBILE_WALLET"),
    [options]
  );
  const offerablePayerCodes = useMemo(() => {
    const set = new Set<string>();
    for (const o of methodOptions) {
      if (o.payerCode) set.add(o.payerCode);
    }
    return set;
  }, [methodOptions]);

  const carriers = useMemo(() => {
    if (optionsLoading) return [];
    if (methodOptions.length === 0) return [];
    const available = (payers ?? []).filter(
      (p) => p.effectiveStatus === "AVAILABLE"
    );
    const scoped =
      offerablePayerCodes.size > 0
        ? available.filter((p) => offerablePayerCodes.has(p.payerCode))
        : available;
    return scoped
      .map((p) => {
        const name = catalogPayerDisplayName(p.payerCode, payers);
        return name ? { id: p.payerCode, name } : null;
      })
      .filter((row): row is { id: string; name: string } => row != null)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [payers, optionsLoading, methodOptions, offerablePayerCodes]);

  const [carrierId, setCarrierId] = useState<string | null>(
    value?.carrierId ?? null
  );
  const [phone, setPhone] = useState(value?.phone ?? "");

  const carriersLoading = Boolean(optionsLoading) || loading;
  const valid =
    Boolean(carrierId) &&
    phone.replace(/\D/g, "").length >= 8 &&
    carriers.some((c) => c.id === carrierId);

  return (
    <form
      className="payout-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || !carrierId) return;
        onSubmit({ carrierId, phone });
      }}
    >
      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.carrier")}
          value={carrierId ?? ""}
          colors={fieldColors}
        >
          <select
            style={{ color: bodyCopy, cursor: "pointer" }}
            value={carrierId ?? ""}
            disabled={carriersLoading}
            onChange={(e) => setCarrierId(e.target.value || null)}
          >
            <option value="" />
            {carriers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </FloatingField>
        {carriersLoading && (
          <span className="payout-hint">{t("payout.fields.payerLoading")}</span>
        )}
        {!carriersLoading && carriers.length === 0 && (
          <span className="payout-hint">{t("payout.fields.noPayers")}</span>
        )}
      </div>

      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.phone")}
          value={phone}
          colors={fieldColors}
        >
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            style={{ color: bodyCopy, ...MONO_INPUT }}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </FloatingField>
      </div>

      <Button
        type="submit"
        size="large"
        disabled={!valid}
        background={backgroundBtn}
        color={textColorBtn}
      >
        {t("common.continue")}
      </Button>
    </form>
  );
};
